import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  Col,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ReloadOutlined } from '@ant-design/icons';
import { AppotaPayReferActivationFlow } from '../../components/appotapay/AppotaPayReferActivationFlow';
import {
  fetchAppotaPayPartnerBalance,
  fetchAppotaPaySubAccounts,
  type AppotaPaySubAccountRow,
  type SubAccountStatus,
} from '../../services/appotapaySubAccountsService';
import { formatCurrencyVND } from '../../utils/transactionHelpers';
import { formatVnDateTimeShort } from '../../utils/vnDateTime';
import { EmptyState } from '../../design-system/EmptyState';

const PAGE_SIZE = 20;

const CARD_STYLE: React.CSSProperties = {
  borderRadius: 16,
  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
};

function statusTag(status: SubAccountStatus) {
  switch (status) {
    case 'ACTIVE':
      return <Tag color="success">ACTIVE</Tag>;
    case 'SUSPENDED':
      return <Tag color="error">SUSPENDED</Tag>;
    default:
      return <Tag color="processing">PENDING_ACTIVATION</Tag>;
  }
}

export default function AppotaPaySubAccountsListPage() {
  const [rows, setRows] = useState<AppotaPaySubAccountRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState<SubAccountStatus | 'ALL'>(
    'PENDING_ACTIVATION'
  );
  const [partnerBalance, setPartnerBalance] = useState<number | null>(null);
  const [liabilityVnd, setLiabilityVnd] = useState<number | null>(null);
  const [balanceError, setBalanceError] = useState<string | null>(null);
  const [balanceLoading, setBalanceLoading] = useState(false);

  const loadBalance = useCallback(async (refresh = false) => {
    setBalanceLoading(true);
    try {
      const data = await fetchAppotaPayPartnerBalance(refresh);
      setPartnerBalance(data.partnerBalanceVnd);
      setLiabilityVnd(data.merchantLiabilityVnd);
      setBalanceError(
        data.balanceSuccess === false
          ? data.balanceError || 'Không lấy được số dư AppotaPay'
          : null
      );
    } catch (e: unknown) {
      const ax = e as { response?: { data?: { message?: string } } };
      setPartnerBalance(null);
      setLiabilityVnd(null);
      setBalanceError(
        ax.response?.data?.message || 'Không lấy được số dư AppotaPay'
      );
    } finally {
      setBalanceLoading(false);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchAppotaPaySubAccounts({
        page,
        limit: PAGE_SIZE,
        status: statusFilter === 'ALL' ? undefined : statusFilter,
      });
      if (res.success) {
        setRows(res.data);
        setTotal(res.pagination.total);
      } else {
        setRows([]);
        setTotal(0);
        setError(res.message || 'Không tải được danh sách merchant AppotaPay');
      }
    } catch (e: unknown) {
      const ax = e as { response?: { data?: { message?: string } } };
      setError(ax.response?.data?.message || 'Lỗi tải dữ liệu');
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter]);

  useEffect(() => {
    load();
    loadBalance();
  }, [load, loadBalance]);

  const columns: ColumnsType<AppotaPaySubAccountRow> = [
    {
      title: 'Merchant',
      dataIndex: 'merchantId',
      width: 100,
      render: (_id, row) => (
        <Link to={`/appotapay/merchants/${row.merchantId}`}>
          #{row.merchantId}
          {row.merchantName ? ` · ${row.merchantName}` : ''}
        </Link>
      ),
    },
    {
      title: 'Refer ID',
      dataIndex: 'referId',
      ellipsis: true,
      render: v => v || '—',
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      width: 120,
      render: s => statusTag(s),
    },
    {
      title: 'Số dư AppotaPay',
      dataIndex: 'appotapayBalanceVnd',
      align: 'right',
      width: 150,
      render: v => (v != null ? formatCurrencyVND(v) : '—'),
    },
    {
      title: 'Đồng bộ lúc',
      dataIndex: 'appotapayLastSyncedAt',
      width: 150,
      render: v => (v ? formatVnDateTimeShort(v) : '—'),
    },
    {
      title: 'SĐT',
      dataIndex: 'merchantPhone',
      width: 120,
      render: v => v || '—',
    },
    {
      title: '',
      key: 'actions',
      width: 180,
      render: (_, row) => (
        <Space direction="vertical" size={4}>
          <Link to={`/appotapay/merchants/${row.merchantId}`}>Chi tiết</Link>
          {row.status === 'PENDING_ACTIVATION' && !row.hasCredentials ? (
            <Link to={`/appotapay/merchants/${row.merchantId}?link=1`}>
              Nhập credentials →
            </Link>
          ) : null}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      {balanceError ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="Không đọc được số dư AppotaPay"
          description={balanceError}
        />
      ) : null}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} md={8}>
          <Card style={CARD_STYLE} loading={balanceLoading}>
            <Statistic
              title="Số dư partner AppotaPay"
              value={partnerBalance ?? 0}
              formatter={v =>
                partnerBalance == null ? '—' : formatCurrencyVND(Number(v))
              }
            />
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card style={CARD_STYLE} loading={balanceLoading}>
            <Statistic
              title="Tổng nợ merchant (nội bộ)"
              value={liabilityVnd ?? 0}
              formatter={v => formatCurrencyVND(Number(v))}
            />
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card style={CARD_STYLE} loading={balanceLoading}>
            <Statistic
              title="Thiếu hụt thanh khoản"
              value={
                partnerBalance != null && liabilityVnd != null
                  ? Math.max(0, liabilityVnd - partnerBalance)
                  : 0
              }
              formatter={v =>
                partnerBalance == null || liabilityVnd == null
                  ? '—'
                  : formatCurrencyVND(Number(v))
              }
            />
          </Card>
        </Col>
      </Row>

      <Card
        title="Merchant AppotaPay (REFER)"
        style={CARD_STYLE}
        extra={
          <Space>
            <Button
              icon={<ReloadOutlined />}
              loading={balanceLoading}
              onClick={() => loadBalance(true)}
            >
              Làm mới số dư partner
            </Button>
            <Select
              value={statusFilter}
              style={{ width: 160 }}
              onChange={v => {
                setPage(1);
                setStatusFilter(v);
              }}
              options={[
                { value: 'ALL', label: 'Tất cả trạng thái' },
                { value: 'ACTIVE', label: 'ACTIVE' },
                { value: 'PENDING_ACTIVATION', label: 'PENDING_ACTIVATION' },
                { value: 'SUSPENDED', label: 'SUSPENDED' },
              ]}
            />
            <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>
              Tải lại
            </Button>
          </Space>
        }
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Luồng kích hoạt AppotaPay REFER"
          description={
            <AppotaPayReferActivationFlow compact status="PENDING_ACTIVATION" />
          }
        />

        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="Duyệt KYC trên AnGi ≠ tạo merchant trên portal AppotaPay"
          description={
            <>
              Sau khi duyệt KYC, <strong>không có request tự động</strong> sang
              AppotaPay. Vận hành vào portal AppotaPay (Master Merchant → Sub
              Merchant) tạo merchant loại <strong>REFER</strong>, lấy
              credentials rồi nhập tại menu này hoặc từ trang Duyệt KYC.
            </>
          }
        />

        {error && (
          <Alert type="error" message={error} style={{ marginBottom: 16 }} />
        )}

        <Table
          rowKey={row => String(row.merchantId || row.id)}
          loading={loading}
          columns={columns}
          dataSource={rows}
          scroll={{ x: 1000 }}
          locale={{
            emptyText: (
              <EmptyState
                description="Chưa có merchant nào — sau khi nhập credentials sẽ hiện ở đây. Vào Duyệt KYC → Nhập credentials."
                showActions={false}
              />
            ),
          }}
          pagination={{
            current: page,
            pageSize: PAGE_SIZE,
            total,
            onChange: setPage,
            showSizeChanger: false,
          }}
        />
      </Card>
    </div>
  );
}
